import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { UserRoles } from '@anytag/graphql';
import { Option } from '@anytag/shared-types';
import { ButtonSubmit, Form } from '@anytag/shared-atoms';
import {
  useAllStaffOptions,
  useTranslateOptions,
  useAllCountryOptions,
  useAllAvailableCountryOptions,
  useAllAdvertisersForSearchOptions
} from '@anytag/shared-hooks';
import {
  TextField,
  RadiosField,
  PasswordField,
  CheckBoxField,
  SelectorField,
  SwitchToggleField
} from '@anytag/shared-molecules';
import { DEFAULT_VALUES } from './utils';
import { UserLinkedFormValues, UserLinkedFormKeys } from './types';

export const Template = () => {
  const { t } = useTranslation();
  const {
    watch,
    reset,
    setValue,
    formState: { isSubmitting }
  } = useFormContext<UserLinkedFormValues>();
  const values = watch();
  const { allCountryOptions } = useAllCountryOptions();
  const { allAdvertiserOptions } = useAllAdvertisersForSearchOptions();
  const { allAvailableCountryOptions } = useAllAvailableCountryOptions();
  const { allStaffOptions, loading: fetchingStaffs } = useAllStaffOptions({
    variables: { offset: 0, limit: 1000, countryId: values.countryId }
  });
  const userTypeOptions: Option<UserLinkedFormValues['userType'], string>[] = useTranslateOptions([
    { value: UserRoles.STAFF, label: 'Staff' },
    { value: UserRoles.ADVERTISER, label: 'Advertiser' }
  ]);

  return (
    <>
      <Form.Body className="form-container">
        <Form.SectionTitle index={1} label={t('User Type')} />

        <RadiosField<UserLinkedFormKeys, UserLinkedFormValues['userType']>
          name="userType"
          options={userTypeOptions}
          onChange={(_, value) => reset({ ...DEFAULT_VALUES[value], advertiserId: values.advertiserId })}
        />

        <Form.SectionTitle index={2} className="mt-6" label={t('User Information')} />

        <SelectorField<UserLinkedFormKeys>
          title={t('Selector.Advertiser')}
          name="advertiserId"
          help={t('Tooltip.User Advertiser')}
          options={allAdvertiserOptions}
          disabled
          required
        />

        {values.userType === UserRoles.STAFF ? (
          <SelectorField<UserLinkedFormKeys>
            name="staffIds"
            loading={fetchingStaffs}
            options={allStaffOptions}
            title={t('Selector.Staff')}
            placeholder={t<string>('Selector.Select staff users')}
            searchProps={{ alwaysShow: true }}
            selectAllProps={{
              alwaysShow: true,
              rightSection: (
                <div className="ml-auto w-37.5" onClick={(e) => e.stopPropagation()}>
                  <SelectorField<UserLinkedFormKeys>
                    name="countryId"
                    options={allCountryOptions}
                    placeholder={t<string>('Selector.Country')}
                    onChange={(name, value) => {
                      setValue(name, value);
                      setValue('staffIds', []);
                    }}
                  />
                </div>
              )
            }}
            multiple
          />
        ) : (
          <>
            <TextField<UserLinkedFormKeys>
              name="name"
              title={t('TextForm.Name')}
              help={t('Tooltip.Username')}
              placeholder={t<string>('NameExample')}
              required
            />

            <Form.Fields itemsPerRow={2}>
              <TextField<UserLinkedFormKeys>
                name="email"
                title={t('TextForm.Email Address')}
                help={t('Tooltip.User Email Address')}
                placeholder="anytag@email.com"
                required
              />
              <SelectorField<UserLinkedFormKeys>
                title={t('Selector.Country')}
                name="countryId"
                help={t('Tooltip.User Country')}
                options={allAvailableCountryOptions}
                required
              />
            </Form.Fields>

            {!values.hasInvitationEmail ? (
              <Form.Fields itemsPerRow={2}>
                <PasswordField<UserLinkedFormKeys>
                  name="password"
                  help={t('Tooltip.User Password')}
                  title={t('Password.Password')}
                  dynamicError
                  required
                />

                <PasswordField<UserLinkedFormKeys>
                  help={t('Tooltip.User Password')}
                  name="passwordConfirm"
                  title={t('Password.Confirm Password')}
                  required
                  dynamicError
                />
              </Form.Fields>
            ) : null}

            <CheckBoxField<UserLinkedFormKeys>
              name="hasInvitationEmail"
              label={t('Send invitation email')}
              onChange={(_: string, value: boolean) => {
                reset({ ...values, password: '', passwordConfirm: '', hasInvitationEmail: value });
              }}
            />

            <SwitchToggleField<UserLinkedFormKeys>
              name="notificationSetting"
              help={t('Tooltip.Notification settings help')}
              title={t('TextForm.Notification Settings')}
            />
          </>
        )}
      </Form.Body>

      <Form.Footer>
        <ButtonSubmit title={t('Button.Save')} loading={isSubmitting} />
      </Form.Footer>
    </>
  );
};
